import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { useTour } from './useTour';
import type { TourStep } from './types';

/** Retry a step target that hasn't mounted yet before giving up. */
const TARGET_RETRY_MS = 60;
const TARGET_MAX_TRIES = 20;

/** Locate the current step's target element, retrying briefly for async mounts.
 *  Scrolls it into view when the step asks. */
function useTargetElement(step: TourStep | null): HTMLElement | null {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!step) {
      setEl(null);
      return;
    }
    let tries = 0;
    let timer = 0;
    const find = () => {
      const found = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
      if (found) {
        if (step.scrollIntoView) found.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setEl(found);
        return;
      }
      if (tries++ < TARGET_MAX_TRIES) timer = window.setTimeout(find, TARGET_RETRY_MS);
      else setEl(null);
    };
    find();
    return () => window.clearTimeout(timer);
  }, [step]);
  return el;
}

export function TourOverlay() {
  const { active, total, position, next, prev, close } = useTour();
  const step = active ? active.steps[active.index]! : null;
  const target = useTargetElement(step);
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: Boolean(step),
    placement: step?.placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip({ padding: 8 }), shift({ padding: 8 }), arrow({ element: arrowRef })],
  });

  useEffect(() => {
    refs.setReference(target);
  }, [target, refs]);

  // Track the target rect for the spotlight cut-out.
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    const update = () => setRect(target.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [target]);

  // Keyboard: Esc closes, arrows step through (ignoring typing in form fields).
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable === true;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (!typing && e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (!typing && e.key === 'ArrowLeft' && position > 1) {
        e.preventDefault();
        prev();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, close, next, prev, position]);

  // Move focus into the popover as its card mounts (each step, via `key`) so
  // keyboard/SR users follow. A ref callback fires once FloatingPortal actually
  // mounts the card — an effect keyed on step id would run one render too early.
  const focusCard = useCallback((node: HTMLDivElement | null) => {
    node?.focus({ preventScroll: true });
  }, []);

  if (!step) return null;

  return (
    <FloatingPortal>
      {rect && (
        <div
          aria-hidden
          className="fixed z-[60] rounded-xl tour-spotlight pointer-events-none transition-[top,left,width,height] duration-300 ease-out"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}
      <div ref={refs.setFloating} style={floatingStyles} className="z-[61]">
        <FloatingArrow ref={arrowRef} context={context} className="fill-white stroke-line" strokeWidth={1} />
        <div
          key={step.id}
          ref={focusCard}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-labelledby="tour-title"
          aria-describedby="tour-body"
          className="animate-tour-pop-in w-[336px] max-w-[calc(100vw-24px)] rounded-2xl border border-line bg-white text-navy shadow-tour-popover p-5 focus:outline-none"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                Step {position} of {total}
              </p>
              <h2 id="tour-title" className="mt-1 text-[15px] font-semibold leading-snug text-navy">
                {step.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close tour"
              className="-mr-1.5 -mt-1.5 shrink-0 rounded-md p-1 text-muted transition-colors hover:bg-line/60 hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-electric"
            >
              <X className="size-4" />
            </button>
          </div>
          <p id="tour-body" className="mt-2 text-[13px] leading-relaxed text-muted">{step.body}</p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-line" aria-hidden="true">
              <div
                className="h-full rounded-full bg-electric transition-[width] duration-300 ease-out"
                style={{ width: `${(position / total) * 100}%` }}
              />
            </div>
            <div className="flex items-center gap-2">
              {position > 1 && (
                <button
                  type="button"
                  onClick={prev}
                  className="rounded-md px-3 py-1.5 text-[13px] font-medium text-muted transition-colors hover:bg-line/60 hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-electric"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={next}
                className="inline-flex items-center gap-1 rounded-md bg-electric px-3.5 py-1.5 text-[13px] font-semibold text-navy shadow-sm transition-[background-color,transform] hover:bg-electric/90 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-electric"
              >
                {position === total ? 'Done' : 'Next'}
                {position !== total && <ChevronRight className="size-3.5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </FloatingPortal>
  );
}
